import { useState } from "react";
import { Phone, MessageCircle, MapPin, Clock, Mail, CheckCircle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

const ContactPage = () => {
  const phoneNumber = "05075537051";
  const whatsappNumber = "905075537051";
  const whatsappMessage = encodeURIComponent("Merhaba, eşya satmak istiyorum. Bilgi alabilir miyim?");
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    await new Promise((resolve) => setTimeout(resolve, 1000));

    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ name: "", phone: "", message: "" });
    toast.success("Mesajınız başarıyla gönderildi! En kısa sürede size dönüş yapacağız.");
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Telefon",
      value: "0507 553 70 51",
      link: `tel:${phoneNumber}`,
      color: "text-[#002FA7]",
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      value: "0507 553 70 51",
      link: whatsappUrl,
      color: "text-[#25D366]",
    },
    {
      icon: MapPin,
      title: "Konum",
      value: "İstanbul, Türkiye",
      link: null,
      color: "text-[#FF3B30]",
    },
    {
      icon: Clock,
      title: "Çalışma Saatleri",
      value: "7/24 Hizmet",
      link: null,
      color: "text-[#128C7E]",
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-slate-50 py-16 md:py-24" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-[#002FA7] font-semibold text-sm uppercase tracking-widest mb-3">
              İLETİŞİM
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight mb-6">
              Bizimle İletişime Geçin
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              İkinci el eşyanızı satmak veya fiyat teklifi almak için hemen bize ulaşın. 7/24 hizmetinizdeyiz!
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 border-b border-slate-200" data-testid="contact-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <div
                key={index}
                className="p-6 bg-white rounded-xl border border-slate-200 card-hover"
                data-testid={`contact-info-${index}`}
              >
                <info.icon className={`w-8 h-8 ${info.color} mb-4`} />
                <h3 className="text-sm text-slate-500 mb-1">{info.title}</h3>
                {info.link ? (
                  <a
                    href={info.link}
                    target={info.link.startsWith("https") ? "_blank" : undefined}
                    rel={info.link.startsWith("https") ? "noopener noreferrer" : undefined}
                    className="text-lg font-semibold text-slate-900 hover:text-[#002FA7] transition-colors"
                  >
                    {info.value}
                  </a>
                ) : (
                  <p className="text-lg font-semibold text-slate-900">{info.value}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form and Map */}
      <section className="py-16 md:py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-6">
                Bize Mesaj Gönderin
              </h2>
              <p className="text-slate-600 mb-8">
                Formu doldurun, size en kısa sürede dönüş yapalım. Veya doğrudan WhatsApp'tan ulaşın.
              </p>

              {isSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-8 text-center" data-testid="form-success">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-green-800 mb-2">Mesajınız Gönderildi!</h3>
                  <p className="text-green-700 mb-4">En kısa sürede size dönüş yapacağız.</p>
                  <Button
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="border-green-500 text-green-700 hover:bg-green-100"
                    data-testid="send-another-btn"
                  >
                    Yeni Mesaj Gönder
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">Adınız Soyadınız</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Adınızı girin"
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-slate-700">Telefon Numaranız</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="05XX XXX XX XX"
                      className="mt-1"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-slate-700">Mesajınız</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Satmak istediğiniz eşya hakkında bilgi verin..."
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#002FA7] hover:bg-[#001d6e] text-white py-3"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? "Gönderiliyor..." : "Mesaj Gönder"}
                  </Button>
                </form>
              )}

              {/* Quick Contact */}
              <div className="mt-8 p-6 bg-slate-50 rounded-xl">
                <p className="text-slate-600 text-sm mb-4">
                  Hızlı yanıt için doğrudan arayın veya WhatsApp'tan yazın:
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`tel:${phoneNumber}`}
                    className="cta-primary flex-1 justify-center"
                    data-testid="contact-quick-phone-btn"
                  >
                    <Phone className="w-5 h-5" />
                    <span>Hemen Ara</span>
                  </a>
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cta-whatsapp flex-1 justify-center"
                    data-testid="contact-quick-whatsapp-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div>
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-6">
                Hizmet Alanımız
              </h2>
              <p className="text-slate-600 mb-6">
                İstanbul'un tüm ilçelerine hizmet veriyoruz. Eşyanızı adresinizden alıyor, nakit ödemeyi anında yapıyoruz.
              </p>

              {/* Map placeholder - Istanbul */}
              <div className="bg-slate-100 rounded-xl overflow-hidden h-[400px] relative" data-testid="map-placeholder">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d385396.3016456806!2d28.731993436406!3d41.00498232948915!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14caa7040068086b%3A0xe1ccfe98bc01b0d0!2zxLBzdGFuYnVs!5e0!3m2!1str!2str!4v1704067200000!5m2!1str!2str"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="İstanbul Haritası"
                />
              </div>

              {/* Service Areas */}
              <div className="mt-6">
                <h3 className="font-semibold text-slate-900 mb-3">Hizmet Verdiğimiz Bölgeler</h3>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Kadıköy", "Üsküdar", "Beşiktaş", "Şişli", "Bakırköy", 
                    "Beylikdüzü", "Ataşehir", "Maltepe", "Pendik", "Kartal",
                    "Fatih", "Beyoğlu", "Sarıyer", "Başakşehir", "Esenyurt"
                  ].map((area) => (
                    <span key={area} className="px-3 py-1 bg-slate-100 rounded-full text-sm text-slate-600">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#002FA7]" data-testid="contact-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4">
            Hemen Arayın, Teklif Alın!
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            7/24 ulaşılabilir hattımızdan bize ulaşın veya WhatsApp'tan fotoğraf gönderin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${phoneNumber}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#002FA7] px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-100 transition-colors"
              data-testid="contact-cta-phone-btn"
            >
              <Phone className="w-6 h-6" />
              <span>0507 553 70 51</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
